import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plus } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api } from "@/lib/api";
import { inputCls, Field, Btn, Th, Td, TableWrap, EmptyRow } from "@/components/admin/ui";

const PRIORITIES = ["low", "normal", "high"];

export default function CrmFollowups() {
  const [items, setItems] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [view, setView] = useState("open");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ carrier_id: "", note: "", next_action: "", due_date: "", priority: "normal" });
  const { carrier_id } = useParams();

  const load = useCallback(() => {
    const params = {};
    if (carrier_id) params.carrier_id = carrier_id;
    if (view !== "all") params.view = view;
    api.get("/crm/followups", { params }).then(({ data }) => setItems(data)).catch(() => {});
  }, [view, carrier_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/crm/carriers").then(({ data }) => {
      setCarriers(data);
      if (carrier_id) setForm((f) => ({ ...f, carrier_id }));
    }).catch(() => {});
  }, [carrier_id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/crm/followups", { ...form, due_date: form.due_date || null, next_action: form.next_action || null });
      toast.success("Follow-up logged");
      setForm({ carrier_id: carrier_id || "", note: "", next_action: "", due_date: "", priority: "normal" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const markDone = async (id) => {
    try {
      await api.patch(`/crm/followups/${id}`, { done: true });
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-followups-page">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl">Follow-ups</h1>
          <div className="flex flex-wrap gap-2">
            <select data-testid="followups-view-filter" value={view} onChange={(e) => setView(e.target.value)} className={`${inputCls} w-auto bg-[#0B0B0B]`}>
              <option value="open">Open</option>
              <option value="overdue">Overdue</option>
              <option value="done">Done</option>
              <option value="all">All</option>
            </select>
            <Btn data-testid="followups-add-toggle" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Log Follow-up</Btn>
          </div>
        </div>

        {showForm && (
          <form onSubmit={save} className="border border-white/15 bg-[#101010] p-5 sm:p-7 mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="followup-add-form">
            <Field label="Carrier *" id="f-carrier">
              <select id="f-carrier" data-testid="followup-carrier-select" required value={form.carrier_id} onChange={(e) => setForm({ ...form, carrier_id: e.target.value })} className={`${inputCls} bg-[#101010]`}>
                <option value="">Select carrier...</option>
                {carriers.map((c) => <option key={c.id} value={c.id}>{c.carrier_code} — {c.company}</option>)}
              </select>
            </Field>
            <Field label="Due Date" id="f-due"><input id="f-due" data-testid="followup-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} /></Field>
            <Field label="Priority" id="f-priority">
              <select id="f-priority" data-testid="followup-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={`${inputCls} bg-[#101010]`}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Contact Note *" id="f-note"><input id="f-note" data-testid="followup-note-input" required value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} placeholder="Called about insurance renewal..." /></Field>
            </div>
            <Field label="Next Action" id="f-next"><input id="f-next" data-testid="followup-next-input" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className={inputCls} placeholder="Send updated COI" /></Field>
            <div className="lg:col-span-3">
              <Btn type="submit" data-testid="followup-save-button" disabled={saving}>{saving ? "Saving..." : "Save Follow-up"}</Btn>
            </div>
          </form>
        )}

        <TableWrap testid="followups-table">
          <thead>
            <tr className="border-b border-white/10"><Th>Carrier</Th><Th>Note</Th><Th>Next Action</Th><Th>Due</Th><Th>Priority</Th><Th>Logged</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <EmptyRow cols={7}>No follow-ups found</EmptyRow>
            ) : (
              items.map((f) => (
                <tr key={f.id} className={`border-b border-white/5 ${f.overdue ? "bg-red-400/5" : ""}`} data-testid={`followup-row-${f.id}`}>
                  <Td className="text-neutral-300 whitespace-nowrap">{f.carrier_name}</Td>
                  <Td className="max-w-[260px]">{f.note}</Td>
                  <Td className="text-neutral-400">{f.next_action || "—"}</Td>
                  <Td>
                    {f.due_date ? (
                      <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${f.overdue ? "text-red-400" : "text-neutral-400"}`} data-testid={f.overdue ? `followup-overdue-${f.id}` : undefined}>
                        {f.overdue ? "Overdue · " : ""}{f.due_date}
                      </span>
                    ) : "—"}
                  </Td>
                  <Td>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 ${f.priority === "high" ? "bg-white text-black" : "border border-white/20 text-neutral-400"}`}>
                      {f.priority}
                    </span>
                  </Td>
                  <Td className="font-mono text-xs text-neutral-500">{new Date(f.created_at).toLocaleDateString()}</Td>
                  <Td>
                    {f.done ? (
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-600">Done</span>
                    ) : (
                      <button data-testid={`followup-done-${f.id}`} onClick={() => markDone(f.id)} className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white">
                        <Check size={13} /> Done
                      </button>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </TableWrap>
      </main>
    </div>
  );
}
